import type { Route } from "./router";

/**
 * Where Fieldpath is, for the links this app makes to it: the header, its admin page, and a
 * deployment's Fieldpath task (/mobilizations/<id>, which finds the task linked to it).
 *
 * A `VITE_` value is baked in AT BUILD TIME, so changing it always means a deploy. The optional
 * `FIELDPATH_URL` repo variable (passed by deploy.yml) only saves a code change, not a deploy.
 * `||` rather than `??`, because an unset repo variable arrives as "" — which `??` would keep,
 * giving a link to nowhere.
 */
export const fieldpathUrl: string =
  import.meta.env.VITE_FIELDPATH_URL || "https://erpapp.energyrenewalpartners.com";

/**
 * This app's help is FIELDPATH'S Help (Jeremy, 19 Oct 2026; built 22 Oct): one set of articles
 * for Canopy, kept in Fieldpath's `web/src/help/articles/` with `screens: [scheduler-…]`. This app
 * links to it rather than carrying a copy, as it does for the admin page.
 *
 * A screen's ? names the SCREEN, `/help/for/<name>`, never an article id: Fieldpath opens the one
 * article for it, or lists several, so this repo holds no list of articles to keep in step.
 * The names must be ones Fieldpath knows (`SCHEDULER_SCREENS` in its `web/src/help/screens.ts`);
 * an unknown one lands on the whole help, saying no article covers it.
 */
export type HelpScreen =
  | "scheduler-calendar"
  | "scheduler-requests"
  | "scheduler-wfh"
  | "scheduler-approvals"
  | "scheduler-field"
  | "scheduler-deployment"
  | "scheduler-hr"
  | "scheduler-actas";

export function helpUrl(screen?: HelpScreen): string {
  return screen ? `${fieldpathUrl}/help/for/${screen}` : `${fieldpathUrl}/help`;
}

/**
 * Each route's help screen. A `Record`, so a new route fails the typecheck until it has one.
 * `deployment` and an old `/field/<id>` link are both one deployment's page.
 */
export const ROUTE_HELP: Record<Route["kind"], HelpScreen> = {
  calendar: "scheduler-calendar",
  mine: "scheduler-requests",
  wfh: "scheduler-wfh",
  approvals: "scheduler-approvals",
  field: "scheduler-field",
  deployment: "scheduler-deployment",
  legacyField: "scheduler-deployment",
  hr: "scheduler-hr",
};
